import { useMemo, useState } from "react";
import {
  Alert,
  Button,
  FormField,
  MultiSelect,
  TextArea,
  TextInput,
  ToggleInput,
} from "@a2z/ui";
import type { FeatureFlagView } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useDialogDismiss } from "@/lib/hooks/useDialogDismiss";
import { useCreateFeatureFlagMutation } from "../services/useCreateFeatureFlagMutation";
import { useUpdateFeatureFlagMutation } from "../services/useUpdateFeatureFlagMutation";
import { useStoreOptionsQuery } from "../services/useStoreOptionsQuery";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  // Present = edit mode (key locked); absent = create mode.
  flag?: FeatureFlagView;
};

type Draft = {
  key: string;
  nameEn: string;
  nameAr: string;
  descriptionEn: string;
  descriptionAr: string;
  isActive: boolean;
  enableForAllStores: boolean;
  storeIds: string[];
};

function draftFromFlag(flag: FeatureFlagView | undefined): Draft {
  if (!flag) {
    return {
      key: "",
      nameEn: "",
      nameAr: "",
      descriptionEn: "",
      descriptionAr: "",
      isActive: true,
      enableForAllStores: false,
      storeIds: [],
    };
  }
  return {
    key: flag.key,
    nameEn: flag.nameEn,
    nameAr: flag.nameAr ?? "",
    descriptionEn: flag.descriptionEn ?? "",
    descriptionAr: flag.descriptionAr ?? "",
    isActive: flag.isActive,
    enableForAllStores: flag.enableForAllStores,
    storeIds: flag.targetStores.map((store) => store.id),
  };
}

// Create/edit dialog. The store allow-list is a client-filtered MultiSelect fed
// by the admin stores endpoint (see useStoreOptionsQuery). Overlay + form layout
// mirror AddAppConfigDialog.
export function FeatureFlagFormDialog({ isOpen, onClose, flag }: Props) {
  const t = useTranslations();
  const isEdit = Boolean(flag);
  const create = useCreateFeatureFlagMutation();
  const update = useUpdateFeatureFlagMutation();
  // Re-seed the draft whenever the target flag changes (keyed remount from the
  // parent guarantees this — see the `key` prop on the caller).
  const [draft, setDraft] = useState<Draft>(() => draftFromFlag(flag));
  const [error, setError] = useState<string | null>(null);
  useDialogDismiss(isOpen, onClose);

  // Only load the (potentially large) store list while the picker is relevant.
  const storeOptions = useStoreOptionsQuery(
    isOpen && !draft.enableForAllStores,
  );
  const multiSelectOptions = useMemo(
    () =>
      storeOptions.options.map((option) => ({
        value: option.value,
        label: option.label,
      })),
    [storeOptions.options],
  );

  if (!isOpen) return null;

  const isPending = create.isPending || update.isPending;

  const update_ = <K extends keyof Draft>(field: K, value: Draft[K]) =>
    setDraft((prev) => ({ ...prev, [field]: value }));

  const nameMissing = draft.nameEn.trim().length === 0;
  const keyMissing = !isEdit && draft.key.trim().length === 0;
  const targetingMissing =
    !draft.enableForAllStores && draft.storeIds.length === 0;
  const submitDisabled =
    isPending || nameMissing || keyMissing || targetingMissing;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      if (isEdit && flag) {
        await update.mutateAsync({
          id: flag.id,
          nameEn: draft.nameEn.trim(),
          nameAr: draft.nameAr.trim(),
          descriptionEn: draft.descriptionEn.trim(),
          descriptionAr: draft.descriptionAr.trim(),
          isActive: draft.isActive,
          enableForAllStores: draft.enableForAllStores,
          // When "all stores" is on, omit storeIds so the curated allow-list is
          // preserved (and restored if the admin toggles back). Otherwise send
          // the picked stores — the backend replaces the list wholesale.
          ...(draft.enableForAllStores ? {} : { storeIds: draft.storeIds }),
        });
      } else {
        await create.mutateAsync({
          key: draft.key.trim(),
          nameEn: draft.nameEn.trim(),
          nameAr: draft.nameAr.trim(),
          descriptionEn: draft.descriptionEn.trim(),
          descriptionAr: draft.descriptionAr.trim(),
          isActive: draft.isActive,
          enableForAllStores: draft.enableForAllStores,
          storeIds: draft.enableForAllStores ? [] : draft.storeIds,
        });
      }
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiJsonClientError
          ? err.message || t(keys.errors.generic)
          : t(keys.errors.generic),
      );
    }
  };

  const showTruncatedHint =
    !draft.enableForAllStores &&
    storeOptions.total > storeOptions.loadedCount;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 px-4 pt-10 pb-[max(2.5rem,env(safe-area-inset-bottom))]"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-[color:var(--border-light)] bg-[color:var(--surface)] p-6 shadow-[var(--shadow-lg)]"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-[color:var(--text-primary)]">
          {isEdit
            ? t(keys.featureFlags.editTitle)
            : t(keys.featureFlags.addTitle)}
        </h2>
        <form onSubmit={handleSubmit} className="mt-4 flex flex-col gap-3">
          {isEdit ? (
            <div className="text-xs text-[color:var(--text-muted)]">
              <span className="font-mono text-[color:var(--text-secondary)]">
                {draft.key}
              </span>
            </div>
          ) : (
            <FormField
              name="key"
              label={t(keys.featureFlags.formKeyLabel)}
              helperText={t(keys.featureFlags.formKeyHelper)}
              required
            >
              <TextInput
                name="key"
                value={draft.key}
                autoFocus
                placeholder="NEW_SELLER_DASHBOARD"
                onChange={(event) =>
                  update_(
                    "key",
                    event.target.value.toUpperCase().replace(/\s+/g, "_"),
                  )
                }
              />
            </FormField>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <FormField
              name="nameEn"
              label={t(keys.featureFlags.formNameEnLabel)}
              required
            >
              <TextInput
                name="nameEn"
                value={draft.nameEn}
                onChange={(event) => update_("nameEn", event.target.value)}
              />
            </FormField>
            <FormField
              name="nameAr"
              label={t(keys.featureFlags.formNameArLabel)}
            >
              <TextInput
                name="nameAr"
                dir="rtl"
                value={draft.nameAr}
                onChange={(event) => update_("nameAr", event.target.value)}
              />
            </FormField>
          </div>

          <FormField
            name="descriptionEn"
            label={t(keys.featureFlags.formDescriptionEnLabel)}
          >
            <TextArea
              name="descriptionEn"
              rows={2}
              value={draft.descriptionEn}
              onChange={(event) =>
                update_("descriptionEn", event.target.value)
              }
            />
          </FormField>
          <FormField
            name="descriptionAr"
            label={t(keys.featureFlags.formDescriptionArLabel)}
          >
            <TextArea
              name="descriptionAr"
              rows={2}
              dir="rtl"
              value={draft.descriptionAr}
              onChange={(event) =>
                update_("descriptionAr", event.target.value)
              }
            />
          </FormField>

          <label className="flex items-start gap-2 rounded-lg border border-[color:var(--border-light)] px-3 py-2">
            <ToggleInput
              name="isActive"
              ariaLabel={t(keys.featureFlags.formActiveLabel)}
              checked={draft.isActive}
              onChange={(event) => update_("isActive", event.target.checked)}
            />
            <span className="text-xs">
              <span className="font-semibold text-[color:var(--text-primary)]">
                {t(keys.featureFlags.formActiveLabel)}
              </span>
              <span className="mt-0.5 block text-[color:var(--text-muted)]">
                {t(keys.featureFlags.formActiveHelper)}
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2 rounded-lg border border-[color:var(--border-light)] px-3 py-2">
            <ToggleInput
              name="enableForAllStores"
              ariaLabel={t(keys.featureFlags.formAllStoresLabel)}
              checked={draft.enableForAllStores}
              onChange={(event) =>
                update_("enableForAllStores", event.target.checked)
              }
            />
            <span className="text-xs">
              <span className="font-semibold text-[color:var(--text-primary)]">
                {t(keys.featureFlags.formAllStoresLabel)}
              </span>
              <span className="mt-0.5 block text-[color:var(--text-muted)]">
                {t(keys.featureFlags.formAllStoresHelper)}
              </span>
            </span>
          </label>

          {!draft.enableForAllStores ? (
            <FormField
              name="storeIds"
              label={t(keys.featureFlags.formStoresLabel)}
              helperText={t(keys.featureFlags.formStoresHelper)}
              required
            >
              <MultiSelect
                options={multiSelectOptions}
                value={draft.storeIds}
                onChange={(value) => update_("storeIds", value)}
                placeholder={t(keys.featureFlags.formStoresPlaceholder)}
                searchPlaceholder={t(
                  keys.featureFlags.formStoresSearchPlaceholder,
                )}
                noResultsLabel={t(keys.featureFlags.formStoresNoResults)}
                aria-label={t(keys.featureFlags.formStoresLabel)}
              />
              {showTruncatedHint ? (
                <p className="mt-1 text-[11px] text-[color:var(--text-muted)]">
                  {t(keys.featureFlags.formStoresTruncatedHint, {
                    loaded: storeOptions.loadedCount,
                    total: storeOptions.total,
                  })}
                </p>
              ) : null}
            </FormField>
          ) : null}

          {error ? <Alert message={error} /> : null}

          <div className="mt-2 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isPending}
              className="h-9 rounded-md px-3 text-xs font-semibold"
            >
              {t(keys.common.cancel)}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={submitDisabled}
              className="h-9 rounded-md px-3 text-xs font-semibold"
            >
              {isPending
                ? t(keys.featureFlags.saving)
                : isEdit
                  ? t(keys.common.save)
                  : t(keys.featureFlags.addAction)}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
